// The things' content (gate C5), read from the tables: items, gathering node kinds, breakable kinds, drop tables,
// interactable kinds, the placements (the lab's layout: seam 5's `{ id, kind, piece, pos, yaw, seed }` rows), and the
// tuning rows. References between them are checked here (a placement naming a missing piece throws, naming it).
import { Gathering, ItemTable, checkDropTables, type DropTable, type GatherNodeDef, type ItemDef, type PickupRules } from "@rpg/items";
import type { InteractTuning } from "@rpg/controller";
import type { SpikeContent } from "../content";
import type { ContentTables } from "../content/types.gen";

export type BreakableKind = ContentTables["breakables"];
export type InteractableKind = ContentTables["interactables"];
export type GatherKind = ContentTables["gatherables"];
export type Placement = ContentTables["things"];

/** Everything the things read, references resolved. */
export interface ThingsContent {
  spike: SpikeContent;
  items: ItemTable;
  itemRows: Map<string, ContentTables["items"]>;
  gathering: Gathering;
  gatherKinds: Map<string, GatherKind>;
  breakables: Map<string, BreakableKind>;
  drops: Map<string, DropTable>;
  interactables: Map<string, InteractableKind>;
  placements: Placement[];
  interact: InteractTuning;
  pickups: PickupRules;
  maxStacks: number;
  unitsPerSlice: number;
}

const DEG = Math.PI / 180;

/** Read the things' tables. Throws naming what is missing or broken. `given` replaces the lab's `things` table (the
 *  streamed valley passes its layout's gatherables, breakables and interactables). */
export function readThingsContent(spike: SpikeContent, given?: readonly Placement[]): ThingsContent {
  const reg = spike.registry;
  const itemRows = new Map(reg.rows("items").map((r) => [r.id, r]));
  const items = new ItemTable(reg.rows("items").map((r): ItemDef => ({ id: r.id, name: r.name.id, stack: r.stack, tags: r.tags })));
  const gatherKinds = new Map(reg.rows("gatherables").map((r) => [r.id, r]));
  const nodeDefs: GatherNodeDef[] = [...gatherKinds.values()].map((r) => ({
    id: r.id,
    yields: r.yields.map((y) => ({ item: y.item.id, min: y.min, max: y.max, ...(y.chance !== undefined ? { chance: y.chance } : {}) })),
    respawn: r.respawn,
    pose: r.pose.id,
    seconds: r.seconds,
  }));
  const gathering = new Gathering(nodeDefs, items);
  const drops = new Map(
    reg.rows("drop-tables").map((r): [string, DropTable] => [
      r.id,
      { id: r.id, ...(r.pick ? { pick: r.pick } : {}), lines: r.lines.map((l) => ({ item: l.item.id, min: l.min, max: l.max, ...(l.chance !== undefined ? { chance: l.chance } : {}), ...(l.weight !== undefined ? { weight: l.weight } : {}) })) },
    ]),
  );
  const dropIssues = checkDropTables([...drops.values()], items);
  if (dropIssues.length) throw new Error(`things: ${dropIssues.join("; ")}`);
  const breakables = new Map(reg.rows("breakables").map((r) => [r.id, r]));
  const interactables = new Map(reg.rows("interactables").map((r) => [r.id, r]));
  const placements = (given ?? reg.rows("things")).map((r) => ({ ...r }));
  const tables: Record<Placement["kind"], Map<string, unknown>> = { gatherable: gatherKinds, breakable: breakables, interactable: interactables };
  for (const p of placements) if (!tables[p.kind].has(p.piece)) throw new Error(`things: placement "${p.id}" names a missing ${p.kind} "${p.piece}"`);
  const missing: string[] = [];
  const need = (id: string): number => {
    const v = reg.get("tuning", id)?.value;
    if (typeof v !== "number") missing.push(id);
    return typeof v === "number" ? v : Number.NaN;
  };
  const interact: InteractTuning = {
    facingHalf: need("interact.facingHalf") * DEG,
    near: need("interact.near"),
    facingWeight: need("interact.facingWeight"),
    tie: need("interact.tie"),
    height: need("interact.height"),
    stick: need("interact.stick"),
  };
  const pickups: PickupRules = { radius: need("things.pickupRadius"), delay: need("things.pickupDelay"), spread: [need("things.pickupSpreadMin"), need("things.pickupSpreadMax")] };
  const maxStacks = need("things.maxStacks");
  const unitsPerSlice = need("nav.unitsPerSlice");
  if (missing.length) throw new Error(`things: missing tuning rows ${missing.join(", ")}`);
  return { spike, items, itemRows, gathering, gatherKinds, breakables, drops, interactables, placements, interact, pickups, maxStacks, unitsPerSlice };
}
